/**
 * AttachmentsDisplay — read-only renderer for the task / idea detail view.
 *
 * 2026-06 upgrade: mirrors the Submissions file-grid UI — aspect-[4/3]
 * object-contain thumbnails with a filename gradient overlay, and a
 * full-size preview modal (image / PDF iframe / video / download fallback)
 * matching TaskSubmissions' FilePreviewOverlay.
 *
 * Tolerates legacy string entries (just URLs without metadata).
 */
import { useEffect, useState } from 'react';
import { Download, Trash2, FileText, Film, ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { toast } from 'sonner';

function kindOf(att) {
  if (typeof att === 'string') {
    const s = att.toLowerCase();
    if (/\.(png|jpe?g|gif|webp)(\?|$)/.test(s)) return 'image';
    if (/\.(mp4|mov|avi|webm)(\?|$)/.test(s)) return 'video';
    if (/\.pdf(\?|$)/.test(s)) return 'pdf';
    return 'file';
  }
  const mime = (att?.mime || att?.content_type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime === 'application/pdf') return 'pdf';
  const name = (att?.name || att?.url || '').toLowerCase();
  if (/\.(png|jpe?g|gif|webp)$/.test(name)) return 'image';
  if (/\.(mp4|mov|avi|webm)$/.test(name)) return 'video';
  if (/\.pdf$/.test(name)) return 'pdf';
  return 'file';
}

function urlOf(att) {
  return typeof att === 'string' ? att : (att?.url || '');
}

function nameOf(att) {
  if (typeof att === 'string') {
    try { return decodeURIComponent(att.split('/').pop() || att); } catch { return att; }
  }
  return att?.name || att?.url || 'file';
}

function prettyBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// Full-size preview modal — mirrors TaskSubmissions' FilePreviewOverlay,
// with mini-gallery prev/next navigation (buttons + arrow keys).
function AttachmentPreviewModal({ att, onClose, onPrev, onNext, index, total, items = [], onJump }) {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft' && onPrev) { e.preventDefault(); onPrev(); }
      if (e.key === 'ArrowRight' && onNext) { e.preventDefault(); onNext(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onPrev, onNext]);
  if (!att) return null;
  const kind = kindOf(att);
  const url = urlOf(att);
  const name = nameOf(att);
  const size = typeof att === 'object' ? att.size : null;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="sm:max-w-[80vw] sm:h-[85vh] bg-card border-border/40 p-0 overflow-hidden flex flex-col"
        data-testid="attachment-preview-overlay"
      >
        <div className="flex items-center justify-between px-4 py-2 border-b border-border/30">
          <div className="flex items-center gap-2 min-w-0">
            {kind === 'video' ? (
              <Film className="h-4 w-4 shrink-0 text-muted-foreground/70" />
            ) : (
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground/70" />
            )}
            <span className="text-[12px] font-medium truncate">{name}</span>
            {!!size && <span className="text-[10px] text-muted-foreground/50 shrink-0">· {prettyBytes(size)}</span>}
            {typeof index === 'number' && total > 1 && (
              <span className="text-[10px] text-muted-foreground/50 shrink-0 tabular-nums" data-testid="attachment-gallery-counter">· {index + 1} of {total}</span>
            )}
          </div>
          <a
            href={url}
            download={name}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gold/10 text-gold text-[11px] hover:bg-gold/15 mr-6"
            data-testid="attachment-preview-download-btn"
          >
            <Download className="h-3.5 w-3.5" /> Download
          </a>
        </div>
        <div className="flex-1 overflow-auto bg-background/50 flex items-center justify-center relative">
          {onPrev && (
            <button
              type="button"
              onClick={onPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-background/70 backdrop-blur border border-border/40 hover:bg-gold/15 hover:border-gold/40 text-foreground/80 hover:text-gold flex items-center justify-center"
              aria-label="Previous attachment"
              data-testid="attachment-preview-prev-btn"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          {onNext && (
            <button
              type="button"
              onClick={onNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-background/70 backdrop-blur border border-border/40 hover:bg-gold/15 hover:border-gold/40 text-foreground/80 hover:text-gold flex items-center justify-center"
              aria-label="Next attachment"
              data-testid="attachment-preview-next-btn"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
          {kind === 'image' && (
            <img src={url} alt={name} className="max-w-full max-h-full object-contain" />
          )}
          {kind === 'pdf' && (
            <iframe src={url} title={name} className="w-full h-full border-0" />
          )}
          {kind === 'video' && (
            <video src={url} controls preload="metadata" className="max-w-full max-h-full" data-testid="attachment-video-player">
              Your browser cannot play this video. Use Download above.
            </video>
          )}
          {kind === 'file' && (
            <div className="text-center p-10">
              <FileText className="h-14 w-14 mx-auto text-muted-foreground/40" />
              <p className="text-[13px] text-foreground/80 mt-3">{name}</p>
              <p className="text-[11px] text-muted-foreground/50 mt-1">
                Preview isn't available for this file type. Use Download above.
              </p>
            </div>
          )}
        </div>
        {total > 1 && (
          <div className="border-t border-border/30 bg-card px-3 py-2 flex items-center gap-2 overflow-x-auto shrink-0" data-testid="attachment-filmstrip">
            {items.map((it, i) => {
              const k = kindOf(it);
              const u = urlOf(it);
              const thumb = (typeof it === 'object' && it.thumbnail_url) || (k === 'image' ? u : null);
              const active = i === index;
              return (
                <button
                  key={typeof it === 'string' ? `legacy-${i}` : (it.id || `att-${i}`)}
                  type="button"
                  onClick={() => onJump(i)}
                  title={nameOf(it)}
                  className={`h-12 w-16 shrink-0 rounded-md overflow-hidden border flex items-center justify-center bg-background/60 transition-colors ${active ? 'border-gold ring-1 ring-gold/50' : 'border-border/30 opacity-60 hover:opacity-100 hover:border-gold/40'}`}
                  data-testid={`attachment-filmstrip-item-${i}`}
                >
                  {thumb ? (
                    <img src={thumb} alt={nameOf(it)} loading="lazy" className="max-w-full max-h-full object-contain" />
                  ) : k === 'video' ? (
                    <Film className="h-5 w-5 text-muted-foreground/50" />
                  ) : (
                    <FileText className={`h-5 w-5 ${k === 'pdf' ? 'text-red-400/60' : 'text-muted-foreground/50'}`} />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AttachmentsDisplay({
  attachments = [],
  onDelete,            // async (attachmentId) => void  — undefined disables delete buttons
  canDelete,           // (attachment) => bool          — per-item delete gate (falls back to onDelete ? true : false)
  testIdPrefix = 'attachments-display',
}) {
  const [previewIdx, setPreviewIdx] = useState(null);  // index into attachments

  if (!attachments || attachments.length === 0) return null;

  const handleDelete = async (att) => {
    if (!onDelete) return;
    const id = typeof att === 'string' ? null : att.id;
    if (!id) {
      toast.error('Cannot delete legacy attachment (no id).');
      return;
    }
    if (!window.confirm(`Delete "${nameOf(att)}"? This cannot be undone.`)) return;
    try {
      await onDelete(id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete attachment');
    }
  };

  return (
    <>
      <div className="space-y-2" data-testid={testIdPrefix}>
        <span className="text-muted-foreground text-[11px] block mb-1 flex items-center gap-1">
          <FileText className="h-3 w-3" /> Attachments
          <span className="ml-1 text-muted-foreground/45">· {attachments.length}</span>
        </span>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`${testIdPrefix}-grid`}>
          {attachments.map((att, i) => {
            const kind = kindOf(att);
            const url = urlOf(att);
            const name = nameOf(att);
            const id = typeof att === 'string' ? `legacy-${i}` : (att.id || `att-${i}`);
            const thumb = (typeof att === 'object' && att.thumbnail_url) || (kind === 'image' ? url : null);
            const allowDelete = onDelete && (canDelete ? canDelete(att) : true);
            return (
              <div
                key={id}
                className="group aspect-[4/3] rounded-lg border border-border/30 bg-background/40 overflow-hidden relative flex items-center justify-center hover:border-gold/40 transition-colors"
                data-testid={`${testIdPrefix}-item-${i}`}
                data-testid-kind={kind}
                title={name}
              >
                <button
                  type="button"
                  onClick={() => setPreviewIdx(i)}
                  className="absolute inset-0 flex items-center justify-center"
                  aria-label={`Preview ${name}`}
                  data-testid={`${testIdPrefix}-open-${i}`}
                >
                  {thumb ? (
                    <img src={thumb} alt={name} loading="lazy" className="max-w-full max-h-full object-contain" />
                  ) : kind === 'video' ? (
                    <Film className="h-12 w-12 text-muted-foreground/40" />
                  ) : (
                    <FileText className={`h-12 w-12 ${kind === 'pdf' ? 'text-red-400/60' : 'text-muted-foreground/40'}`} />
                  )}
                </button>

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1.5 pointer-events-none">
                  <p className="text-[11px] text-white/90 truncate font-medium">{name}</p>
                  {typeof att === 'object' && att.size ? (
                    <p className="text-[9px] text-white/50 tabular-nums">{prettyBytes(att.size)}</p>
                  ) : null}
                </div>

                {/* Action overlay (download + delete) */}
                <div className="absolute top-1.5 right-1.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  {url && (
                    <a
                      href={url}
                      download={name}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-6 w-6 rounded-full bg-background/80 backdrop-blur border border-border/30 hover:bg-gold/15 hover:border-gold/40 text-foreground/70 hover:text-gold flex items-center justify-center"
                      title="Download"
                      data-testid={`${testIdPrefix}-download-${i}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Download className="h-3 w-3" />
                    </a>
                  )}
                  {allowDelete && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDelete(att); }}
                      className="h-6 w-6 rounded-full bg-background/80 backdrop-blur border border-border/30 hover:bg-red-500/15 hover:border-red-500/40 text-foreground/70 hover:text-red-400 flex items-center justify-center"
                      title="Delete"
                      data-testid={`${testIdPrefix}-delete-${i}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {previewIdx !== null && attachments[previewIdx] && (
        <AttachmentPreviewModal
          att={attachments[previewIdx]}
          onClose={() => setPreviewIdx(null)}
          index={previewIdx}
          total={attachments.length}
          onPrev={previewIdx > 0 ? () => setPreviewIdx(previewIdx - 1) : undefined}
          onNext={previewIdx < attachments.length - 1 ? () => setPreviewIdx(previewIdx + 1) : undefined}
          items={attachments}
          onJump={setPreviewIdx}
        />
      )}
    </>
  );
}

export default AttachmentsDisplay;
